import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  fetchMailingsList,
  MAILING_TYPE_CONSENT_UNSIGNED,
  MAILING_TYPE_EVI_NOT_FINISHED,
  useMailingsCapabilities,
  useMailingsRunStatus,
  useMailingPreview,
  useMailingRecheck,
  useMailingSendAll,
  useMailingTestSend,
  type MailingsCapabilitiesDto,
  type MailingsRunStatusDto,
  type MailingDeliveryFilter,
  type MailingOutcomeFilter,
  type MailingRecipientDto,
  type MailingSummaryDto,
} from '../../api/mailings';
import { Badge, Button, EmptyState, ErrorState, Field, Input, Modal, Pagination, Select, Spinner } from '../../components/ui';
import { TableWrap } from '../../components/TableWrap';
import { useQueryParams } from '../../lib/query-params';
import './mailings.css';
import { ConsentUnsignedPanel } from './ConsentUnsignedPanel';
import { LinkReceivedPanel } from './LinkReceivedPanel';
import { MailingListsSection } from './MailingListsSection';
import { MailingListView } from './MailingListView';
import {
  BUCKET_LABELS,
  DELIVERY_FILTERS,
  EMPTY_SUMMARY,
  LOGIC_DESCRIPTION,
  MAILING_TYPE_LABEL,
  MAILING_TYPE_LABELS,
  MAILING_TYPE_DOCUMENTS_UNSIGNED,
  OUTCOME_LABELS,
  OUTCOME_COUNT_KEYS,
  OUTCOME_FILTERS,
  OUTCOME_HINTS,
  PARAM,
  TOTAL_RECIPIENTS_HINT,
  TRACK_LABELS,
  formatCapabilities,
  formatHistoryLine,
  formatLastSend,
  formatRunProgress,
  outcomeCount,
  outcomeCounts,
  parseBucketParam,
  parseDeliveryParam,
  parseOutcomeParam,
  parseTypeParam,
} from './mailings-model';
import {
  LIST_PARAM,
  TAB_CLASSIC,
  TAB_LISTS,
  TAB_TEMPLATES,
  parseListIdParam,
  parsePageParam,
  parseStateParam,
  parseTabParam,
  type ListStateFilter,
} from './lists-model';
import { TemplatesSection } from './TemplatesSection';

/**
 * «Рассылки» — the staff mailing surface.
 *
 * THREE TABS since T.Z134 (`?tab=`, lists is the default):
 *   * «Списки рассылок» — the user-created campaigns of contract #34: the index,
 *     the create fold and, through `?list=<id>`, one list's resolved recipients.
 *     The SEND over a list is the T.Z133 engine (its routes answer since wave 15);
 *   * «Быстрая рассылка» — the FIXED-type flow, THREE types since T.Z182
 *     (`?type=`, «не подписавшим договор» the default): the T.Z124 surface over
 *     contract #32.1–#32.7, the second type «получившим ссылку» over
 *     #32.8–#32.13 (`LinkReceivedPanel`) — the same controls, the second one
 *     with the per-recipient ЕПГУ link and the «не готовы к отправке» counter —
 *     and the third type «Ошибка подписи договора» over #32.14–#32.19
 *     (`ConsentUnsignedPanel`), the five-axis 14–17 no-consent audience with the
 *     T.Z124 track link;
 *   * «Шаблоны» (T.Z134) — the templates management surface over contract #33:
 *     the list with every version, the version view, the editor that always
 *     creates a NEW version, and the archive toggle (see `TemplatesSection`).
 *
 * The fixed flow's own house rules (all still true, now over the three types):
 *   * the inclusion logic is STATED on the page (the first type: both register
 *     buckets; the second: the «Получил ссылку» status and the link rule; the
 *     third: the five-axis 14–17 no-consent page);
 *   * every recipient row previews the exact letter he would receive — in a
 *     sandboxed iframe with NO scripts and NO same-origin access (the letter is
 *     third-party HTML; it never gets to run anything);
 *   * the test letter goes to an address the OPERATOR types, never to the child,
 *     and it writes no ledger row (the api's own rule) — so a test can never
 *     mark a child as served;
 *   * «Отправить всем» confirms first, disables while the paced run drains, and
 *     the progress comes from the run-status read — the api paces itself, the
 *     page only watches (one engine across the three types: a run of any of
 *     them is a 409 for the others, which the notice line shows verbatim);
 *   * delivery states are shown for what they are: ACCEPTED is what SES proves,
 *     DELIVERED only where SES confirms it back (the recheck).
 */

export const PAGE_SIZE = 20;

export function MailingsPage() {
  const { params, setParams } = useQueryParams();
  const tab = parseTabParam(params[LIST_PARAM.TAB] ?? null);
  const listId = parseListIdParam(params[LIST_PARAM.LIST] ?? null);

  return (
    <div className="mailings" data-testid="mailings-page">
      <header className="mailings-head">
        <div>
          <h1>Рассылки</h1>
          <p className="mailings-subtitle">Массовые письма родителям по спискам кампании</p>
        </div>
      </header>

      <nav className="mailings-tabs" data-testid="mailings-tabs" aria-label="Разделы рассылок">
        <button
          type="button"
          className={`mailings-tab${tab === 'lists' ? ' is-active' : ''}`}
          aria-selected={tab === 'lists'}
          role="tab"
          onClick={() => setParams({ [LIST_PARAM.TAB]: null, [LIST_PARAM.LIST]: null })}
          data-testid="mailings-tab-lists"
        >
          Списки рассылок
        </button>
        <button
          type="button"
          className={`mailings-tab${tab === 'classic' ? ' is-active' : ''}`}
          aria-selected={tab === 'classic'}
          role="tab"
          onClick={() =>
            setParams({ [LIST_PARAM.TAB]: TAB_CLASSIC, [LIST_PARAM.LIST]: null, [LIST_PARAM.STATE]: null, [LIST_PARAM.PAGE]: null })
          }
          data-testid="mailings-tab-classic"
        >
          Быстрая рассылка
        </button>
        <button
          type="button"
          className={`mailings-tab${tab === 'templates' ? ' is-active' : ''}`}
          aria-selected={tab === 'templates'}
          role="tab"
          onClick={() =>
            setParams({ [LIST_PARAM.TAB]: TAB_TEMPLATES, [LIST_PARAM.LIST]: null, [LIST_PARAM.STATE]: null, [LIST_PARAM.PAGE]: null })
          }
          data-testid="mailings-tab-templates"
        >
          Шаблоны
        </button>
      </nav>

      {tab === TAB_LISTS ? (
        listId === null ? (
          <MailingListsSection
            onOpen={(id) => setParams({ [LIST_PARAM.LIST]: String(id), [LIST_PARAM.STATE]: null, [LIST_PARAM.PAGE]: null })}
          />
        ) : (
          <MailingListView
            listId={listId}
            state={parseStateParam(params[LIST_PARAM.STATE] ?? null)}
            page={parsePageParam(params[LIST_PARAM.PAGE] ?? null)}
            onNavigate={(next: { state?: ListStateFilter; page?: number }) =>
              setParams({
                [LIST_PARAM.STATE]: next.state && next.state !== 'active' ? next.state : null,
                [LIST_PARAM.PAGE]: next.page && next.page !== 1 ? String(next.page) : null,
              })
            }
            onClose={() => setParams({ [LIST_PARAM.LIST]: null, [LIST_PARAM.STATE]: null, [LIST_PARAM.PAGE]: null })}
          />
        )
      ) : tab === TAB_TEMPLATES ? (
        <TemplatesSection />
      ) : (
        <ClassicTab />
      )}
    </div>
  );
}

/**
 * The classic tab since T.Z140: the FIXED-type flow, now with a REAL choice of
 * type (`?type=`, the first type the default, exactly the prefilled state the
 * page always showed). The three panels are one interface over three mailings —
 * «Рассылка не подписавшим договор» (#32.1–#32.7), «Рассылка получившим
 * ссылку» (#32.8–#32.13) and «Ошибка подписи договора» (#32.14–#32.19,
 * T.Z182) — and the switcher card is the only shared chrome.
 */
function ClassicTab() {
  const { params, setParams } = useQueryParams();
  const type = parseTypeParam(params[PARAM.TYPE] ?? null);

  return (
    <>
      <section className="mailings-desc card" data-testid="mailings-type-card">
        <Field label="Тип рассылки" htmlFor="mailings-type">
          <Select
            id="mailings-type"
            value={type}
            onChange={(event) =>
              setParams({
                // The FIRST type is the tab's default — it keeps the URL bare.
                [PARAM.TYPE]: event.target.value === MAILING_TYPE_DOCUMENTS_UNSIGNED ? null : event.target.value,
                [PARAM.DELIVERY]: null,
                [PARAM.OUTCOME]: null,
                [PARAM.PAGE]: null,
              })
            }
            data-testid="mailings-type"
          >
            <option value={MAILING_TYPE_DOCUMENTS_UNSIGNED}>{MAILING_TYPE_LABELS[MAILING_TYPE_DOCUMENTS_UNSIGNED]}</option>
            <option value={MAILING_TYPE_EVI_NOT_FINISHED}>{MAILING_TYPE_LABELS[MAILING_TYPE_EVI_NOT_FINISHED]}</option>
            <option value={MAILING_TYPE_CONSENT_UNSIGNED}>{MAILING_TYPE_LABELS[MAILING_TYPE_CONSENT_UNSIGNED]}</option>
          </Select>
        </Field>
      </section>
      {type === MAILING_TYPE_EVI_NOT_FINISHED ? (
        <LinkReceivedPanel />
      ) : type === MAILING_TYPE_CONSENT_UNSIGNED ? (
        <ConsentUnsignedPanel />
      ) : (
        <ClassicMailingPanel />
      )}
    </>
  );
}

/** The T.Z124 fixed-type flow — one tab over, and since T.Z140 one of TWO types
 * (the type choice lives in the shared switcher card of {@link ClassicTab}). */
function ClassicMailingPanel() {
  const { params, setParams } = useQueryParams();
  const bucket = parseBucketParam(params[PARAM.BUCKET] ?? null);
  const delivery = parseDeliveryParam(params[PARAM.DELIVERY] ?? null);
  const outcome = parseOutcomeParam(params[PARAM.OUTCOME] ?? null);
  const page = parsePageParam(params[PARAM.PAGE] ?? null);

  const list = useQuery({
    queryKey: ['mailings', 'list', bucket ?? 'ALL', delivery ?? 'ALL', outcome ?? 'ALL', page],
    queryFn: ({ signal }) => fetchMailingsList({ bucket, delivery, outcome, page, pageSize: PAGE_SIZE }, signal),
  });

  const runStatus = useMailingsRunStatus();

  const summary = list.data?.summary ?? EMPTY_SUMMARY;
  const rows = list.data?.data ?? [];

  /**
   * T.Z146 — the outcome view and the bucket chips are TWO answers to «who is
   * the row»: the outcome universe is the JOURNAL (children who signed or were
   * rejected after the send have left the live buckets), so the two are mutually
   * exclusive in the URL — picking one clears the other, the delivery filter
   * ANDs with both.
   */
  const setFilter = (next: { bucket?: MailingRecipientDto['bucket']; delivery?: MailingDeliveryFilter }) => {
    const bucketChanges = next.bucket !== undefined && next.bucket !== bucket;
    setParams({
      [PARAM.BUCKET]: next.bucket ?? null,
      [PARAM.DELIVERY]: next.delivery ?? null,
      [PARAM.OUTCOME]: bucketChanges ? null : outcome ?? null,
      [PARAM.PAGE]: null,
    });
  };

  const setOutcome = (next: MailingOutcomeFilter | undefined) => {
    setParams({
      [PARAM.BUCKET]: null,
      [PARAM.DELIVERY]: delivery ?? null,
      [PARAM.OUTCOME]: next ?? null,
      [PARAM.PAGE]: null,
    });
  };

  return (
    <div className="mailings-classic" data-testid="mailings-classic">
      <section className="mailings-desc card" data-testid="mailings-logic">
        <div className="mailings-filters mailings-filters--plain">
          <h2>Быстрая рассылка: {MAILING_TYPE_LABEL}</h2>
        </div>
        <p>{LOGIC_DESCRIPTION}</p>
      </section>

      <SummaryChips summary={summary} />

      <OutcomeCards summary={summary} outcome={outcome} onPick={setOutcome} />

      <CapabilitiesLine />

      <TestSendCard rows={rows} />

      <SendAllCard runStatus={runStatus.data?.data ?? null} notSent={summary.delivery.notSent} />

      <section className="mailings-list card card--flush" data-testid="mailings-list">
        <div className="mailings-filters">
          <h2>Список получателей</h2>
          <Field label="Список" htmlFor="mailings-bucket">
            <Select
              id="mailings-bucket"
              value={bucket ?? ''}
              onChange={(event) =>
                setFilter({ bucket: (event.target.value || undefined) as MailingRecipientDto['bucket'] | undefined })
              }
              data-testid="mailings-bucket"
            >
              <option value="">Оба списка ({summary.buckets.total})</option>
              <option value="NOT_ARRIVED">Не перешёл в iSmart ({summary.buckets.notArrived})</option>
              <option value="NOT_SIGNED">Не подписал ({summary.buckets.notSigned})</option>
            </Select>
          </Field>
          <Field label="Доставка" htmlFor="mailings-delivery">
            <Select
              id="mailings-delivery"
              value={delivery ?? ''}
              onChange={(event) =>
                setFilter({ delivery: (event.target.value || undefined) as MailingDeliveryFilter | undefined })
              }
              data-testid="mailings-delivery"
            >
              {DELIVERY_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value === ''
                    ? `Все (${summary.buckets.total})`
                    : `${option.label} (${summary.delivery[option.countKey]})`}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Итог" htmlFor="mailings-outcome">
            <Select
              id="mailings-outcome"
              value={outcome ?? ''}
              onChange={(event) => setOutcome((event.target.value || undefined) as MailingOutcomeFilter | undefined)}
              data-testid="mailings-outcome"
            >
              {OUTCOME_FILTERS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value === ''
                    ? `Все (${summary.buckets.total})`
                    : `${option.label} (${outcomeCount(summary, option.value)})`}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {list.isPending ? (
          <Spinner label="Загружаем получателей" />
        ) : list.isError ? (
          <ErrorState message={String(list.error)} onRetry={() => void list.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState title="Никого" hint="Под текущие фильтры не попал ни один ребёнок." />
        ) : (
          <RecipientsTable
            rows={rows}
            page={list.data?.meta.page ?? page}
            total={list.data?.meta.total ?? 0}
            onPage={(next) => setParams({ [PARAM.PAGE]: next === 1 ? null : String(next) })}
          />
        )}
      </section>
    </div>
  );
}


/** The accent tone of one summary tile — the stat-card/teachers-chip vocabulary. */
type ChipTone = 'brand' | 'neutral' | 'ok' | 'bad' | 'warn';

/**
 * T.Z128 — the counters as stat cards, not gray pills. Same grammar as the
 * dashboard's `.stat-card` / the teachers funnel chips: a self-carded tile, an
 * accent bar, the count display-size in `tabular-nums`, the label a small
 * secondary caption. Visual only: the tiles are not controls — the two filters
 * below the table card (the same `?bucket=` / `?delivery=` they always were)
 * remain the way to slice the list.
 */
function SummaryChips({ summary }: { summary: MailingSummaryDto }) {
  const tiles: ReadonlyArray<{ key: string; value: number; label: string; tone: ChipTone; hint?: string }> = [
    {
      key: 'total',
      value: summary.buckets.total,
      label: 'всего получателей',
      tone: 'brand',
      hint: TOTAL_RECIPIENTS_HINT,
    },
    { key: 'not-arrived', value: summary.buckets.notArrived, label: 'не перешли в iSmart', tone: 'warn' },
    { key: 'not-signed', value: summary.buckets.notSigned, label: 'пришли, не подписали', tone: 'warn' },
    { key: 'not-sent', value: summary.delivery.notSent, label: 'не получил письмо', tone: 'neutral' },
    { key: 'accepted', value: summary.delivery.accepted, label: 'принято', tone: 'neutral' },
    { key: 'delivered', value: summary.delivery.delivered, label: 'доставлено', tone: 'ok' },
    { key: 'failed', value: summary.delivery.failed, label: 'ошибка доставки', tone: 'bad' },
    { key: 'ai', value: summary.tracks.ai, label: 'программы ИИ', tone: 'brand' },
    { key: 'python', value: summary.tracks.python, label: 'программы Python', tone: 'brand' },
    {
      key: 'unresolved',
      value: summary.tracks.unresolved,
      label: 'трек не определён',
      tone: 'neutral',
      hint: 'Письмо уйдёт на Python — приветственную страницу того трека.',
    },
  ];
  return (
    <section className="mailings-summary" data-testid="mailings-summary">
      {tiles.map((tile) => (
        <div
          key={tile.key}
          className={`mailings-chip mailings-chip--${tile.tone}`}
          data-testid={`mailings-chip-${tile.key}`}
          title={tile.hint}
        >
          <span className="mailings-chip-value">{tile.value}</span>
          <span className="mailings-chip-label">{tile.label}</span>
        </div>
      ))}
    </section>
  );
}

/**
 * T.Z146 — the two POST-SEND outcome cards the owner asked for: who SIGNED the
 * agreement after the letter went out («Подписали после отправки письма»,
 * T.Z148's time-based wording, green), and who went to «Отказ» («Отказались
 * после рассылки», red). The counts are over the campaign's JOURNAL
 * (`summary.outcomes`, the same fold the api's `?outcome=` filter narrows by),
 * so a child who signed after the send is counted even though he has LEFT the
 * live recipient buckets. Each card deep-links into the table state its filter
 * answers — the tile grammar of the chips above, now a control; the picked card
 * reads as pressed, and clicking it again clears the filter. Picking one clears
 * the plain bucket select: the outcome universe is the journal, the buckets are
 * the live set — two answers to «who is the row» that cannot both hold. T.Z148 —
 * each card's tooltip carries the honest definition ({@link OUTCOME_HINTS}): the
 * count is TIME-based, attribution via letter clicks is not visible to the system.
 */
function OutcomeCards({
  summary,
  outcome,
  onPick,
}: {
  summary: MailingSummaryDto;
  outcome: MailingOutcomeFilter | undefined;
  onPick: (next: MailingOutcomeFilter | undefined) => void;
}) {
  const counts = outcomeCounts(summary);
  const cards: ReadonlyArray<{ key: MailingOutcomeFilter; tone: 'ok' | 'bad' }> = [
    { key: 'signed-after', tone: 'ok' },
    { key: 'declined-after', tone: 'bad' },
  ];
  return (
    <section className="mailings-summary" data-testid="mailings-outcome-cards">
      {cards.map((card) => (
        <button
          key={card.key}
          type="button"
          className={`mailings-chip mailings-chip--${card.tone} mailings-chip--link${outcome === card.key ? ' is-active' : ''}`}
          aria-pressed={outcome === card.key}
          onClick={() => onPick(outcome === card.key ? undefined : card.key)}
          data-testid={`mailings-outcome-${card.key}`}
          title={`${OUTCOME_HINTS[card.key]} ${
            outcome === card.key
              ? 'Показаны в списке ниже — нажать ещё раз, чтобы сбросить.'
              : 'Нажать, чтобы показать этих детей в списке.'
          }`}
        >
          <span className="mailings-chip-value">{counts[OUTCOME_COUNT_KEYS[card.key]]}</span>
          <span className="mailings-chip-label">{OUTCOME_LABELS[card.key]}</span>
        </button>
      ))}
    </section>
  );
}

function CapabilitiesLine() {
  const capabilities = useMailingsCapabilities();
  const value: MailingsCapabilitiesDto | undefined = capabilities.data?.data;
  return (
    <p className="mailings-capabilities" data-testid="mailings-capabilities">
      {formatCapabilities(value)}
    </p>
  );
}

function TestSendCard({ rows }: { rows: MailingRecipientDto[] }) {
  const [applicationId, setApplicationId] = useState<number | null>(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const testSend = useMailingTestSend();
  const selected = rows.find((row) => row.applicationId === applicationId) ?? rows[0] ?? null;

  const submit = () => {
    if (!selected || !email.trim()) return;
    setResult(null);
    testSend.mutate(
      { applicationId: selected.applicationId, email: email.trim(), ...(name.trim() ? { name: name.trim() } : {}) },
      {
        onSuccess: (answer) => setResult(`Письмо ушло на ${answer.data.to} (ссылка: ${answer.data.startUrl})`),
        onError: (error) => setResult(`Ошибка: ${error.message}`),
      },
    );
  };

  return (
    <section className="mailings-test card" data-testid="mailings-test">
      <h2>Письмо тестом</h2>
      <p className="mailings-test-hint">
        Одно письмо на указанный адрес — с именем и ссылкой, как их увидит получатель. Тест не попадает в историю
        рассылки и не мешает «Отправить всем».
      </p>
      <div className="mailings-test-form">
        <Field label="Ребёнок (письмо его данными)" htmlFor="mailings-test-child">
          <Select
            id="mailings-test-child"
            value={selected ? String(selected.applicationId) : ''}
            onChange={(event) => setApplicationId(Number(event.target.value))}
            data-testid="mailings-test-child"
          >
            {rows.map((row) => (
              <option key={row.applicationId} value={row.applicationId}>
                {row.fullName ?? '(без ФИО)'} — {BUCKET_LABELS[row.bucket]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Имя в письме" htmlFor="mailings-test-name">
          <Input
            id="mailings-test-name"
            value={name}
            placeholder={selected?.greetingName ?? 'Имя Отчество'}
            onChange={(event) => setName(event.target.value)}
            data-testid="mailings-test-name"
          />
        </Field>
        <Field label="Куда отправить" htmlFor="mailings-test-email">
          <Input
            id="mailings-test-email"
            type="email"
            value={email}
            placeholder="ваш@ismart.org"
            onChange={(event) => setEmail(event.target.value)}
            data-testid="mailings-test-email"
          />
        </Field>
        <Button onClick={submit} disabled={!email.trim() || !selected || testSend.isPending} data-testid="mailings-test-send">
          {testSend.isPending ? 'Отправляем…' : 'Отправить письмо тестом'}
        </Button>
      </div>
      {result ? (
        <p className="mailings-test-result" data-testid="mailings-test-result">
          {result}
        </p>
      ) : null}
    </section>
  );
}

function SendAllCard({ runStatus, notSent }: { runStatus: MailingsRunStatusDto | null; notSent: number }) {
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const sendAll = useMailingSendAll();
  const running = runStatus?.running ?? false;
  const run = runStatus?.run ?? null;

  const arm = () => {
    setConfirming(false);
    setNotice(null);
    sendAll.mutate(
      {},
      {
        onSuccess: (answer) => setNotice(`Рассылка запущена: ${answer.data.total} получателей (запуск ${answer.data.runId}).`),
        onError: (error) => setNotice(`Не удалось запустить: ${error.message}`),
      },
    );
  };

  return (
    <section className="mailings-sendall card" data-testid="mailings-sendall">
      <h2>Отправить всем</h2>
      <p className="mailings-sendall-hint">
        Письма уходят по очереди, в заданном темпе (по умолчанию 5 в секунду). Запуск продолжается с того места, где остановился: кто
        уже получил письмо — повторно не получает.
      </p>
      <div className="mailings-sendall-actions">
        <Button variant="primary" disabled={running || sendAll.isPending} onClick={() => setConfirming(true)} data-testid="mailings-send-all">
          {running ? 'Отправляется…' : `Отправить всем (${notSent})`}
        </Button>
        <RecheckButton />
      </div>
      {running && run ? (
        <p className="mailings-run-progress" data-testid="mailings-run-progress">
          {formatRunProgress(run)}
        </p>
      ) : null}
      {notice ? (
        <p className="mailings-sendall-notice" data-testid="mailings-sendall-notice">
          {notice}
        </p>
      ) : null}
      <Modal
        open={confirming}
        onClose={() => setConfirming(false)}
        title="Отправить всем?"
        description={`Будет отправлено ${notSent} писем родителям детей, не подписавших договор. Тем, кто уже получил письмо, повторно не отправляем.`}
        footer={
          <>
            <Button onClick={() => setConfirming(false)}>Отмена</Button>
            <Button variant="primary" onClick={arm} data-testid="mailings-send-all-confirm">
              Отправить
            </Button>
          </>
        }
      >
        <p>{LOGIC_DESCRIPTION}</p>
      </Modal>
    </section>
  );
}

function RecheckButton() {
  const recheck = useMailingRecheck();
  const [notice, setNotice] = useState<string | null>(null);
  return (
    <span className="mailings-recheck">
      <Button
        disabled={recheck.isPending}
        onClick={() =>
          recheck.mutate(undefined, {
            onSuccess: (answer) =>
              setNotice(
                `Проверено: ${answer.data.checked}; доставлено: ${answer.data.delivered}; без подтверждения: ${answer.data.unchanged}`,
              ),
            onError: (error) => setNotice(`Не удалось проверить: ${error.message}`),
          })
        }
        data-testid="mailings-recheck"
      >
        Проверить доставку
      </Button>
      {notice ? (
        <span className="mailings-recheck-notice" data-testid="mailings-recheck-notice">
          {notice}
        </span>
      ) : null}
    </span>
  );
}

/**
 * T.Z128 — the register chrome, the applications register's own (card--flush,
 * `table.data` with its sticky header and hover rows, the shared `Pagination`
 * footer with the «Показаны …» range caption). Only the chrome: the row click
 * still opens the preview and nothing else about the list changed.
 */
function RecipientsTable({
  rows,
  page,
  total,
  onPage,
}: {
  rows: MailingRecipientDto[];
  page: number;
  total: number;
  onPage: (next: number) => void;
}) {
  const [previewId, setPreviewId] = useState<number | null>(null);
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);
  return (
    <>
      <TableWrap>
        <table className="data mailings-table" data-testid="mailings-table">
          <thead>
            <tr>
              <th>Ребёнок</th>
              <th>E-mail</th>
              <th>Список</th>
              <th>Программа</th>
              <th>Трек</th>
              <th>Отправлено</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.applicationId}
                className="mailings-row"
                onClick={() => setPreviewId(row.applicationId)}
                data-testid="mailings-row"
              >
                <td className="cell-primary">{row.fullName ?? '—'}</td>
                <td className="cell-muted">{row.email ?? '—'}</td>
                <td>
                  <Badge tone={row.bucket === 'NOT_ARRIVED' ? 'warn' : 'brand'}>{BUCKET_LABELS[row.bucket]}</Badge>
                </td>
                <td className="mailings-program" title={row.programName ?? undefined}>
                  {row.programName ?? '—'}
                </td>
                <td className="cell-compact">
                  {TRACK_LABELS[row.track]}
                  {!row.trackResolved ? <span className="cell-muted"> (не определён)</span> : null}
                </td>
                <td className="cell-compact cell-muted">{formatLastSend(row)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableWrap>
      <Pagination
        meta={{ page, totalPages: pages, total, limit: PAGE_SIZE }}
        onPage={onPage}
        rangeCaption={`Показаны ${from}–${to} из ${total}`}
      />
      <PreviewModal applicationId={previewId} onClose={() => setPreviewId(null)} />
    </>
  );
}

function PreviewModal({ applicationId, onClose }: { applicationId: number | null; onClose: () => void }) {
  const preview = useMailingPreview(applicationId);
  const letter = preview.data?.data ?? null;
  return (
    <Modal open={applicationId !== null} onClose={onClose} title="Предпросмотр письма" wide>
      {preview.isPending || !letter ? (
        <Spinner label="Готовим предпросмотр" />
      ) : (
        <div className="mailings-preview" data-testid="mailings-preview">
          <p className="mailings-preview-meta">
            {letter.subject} · трек {TRACK_LABELS[letter.track]} · ссылка {letter.startUrl}
          </p>
          {/* The letter is third-party HTML: sandboxed with NO scripts and NO same-origin. */}
          <iframe
            className="mailings-preview-frame"
            title="Предпросмотр письма"
            sandbox=""
            srcDoc={letter.html}
            data-testid="mailings-preview-frame"
          />
          <div className="mailings-history">
            <h3>История писем этому ребёнку</h3>
            {letter.history.length === 0 ? (
              <p data-testid="mailings-history-empty">Ещё не отправлялось.</p>
            ) : (
              <ul data-testid="mailings-history">
                {letter.history.map((message) => (
                  <li key={message.id}>{formatHistoryLine(message)}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

